import React from "react";
import { useParams, Link } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import CTASection from "../components/home/CTASection";
import Seo from "../components/Seo";
import useReveal from "../hooks/useReveal";
import { SERVICES, PROJECTS, IMAGES } from "../mock";
import { useEstimate } from "../components/EstimateContext";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";

const TAG_MAP = {
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  basement: "Basement",
  "full-home": "Full Home",
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const { openEstimate } = useEstimate();
  const service = SERVICES.find((s) => s.slug === slug);
  useReveal([slug]);

  if (!service) {
    return (
      <div className="pt-40 pb-32 text-center">
        <h1 className="font-display text-3xl font-bold text-[#0f2740] mb-4">Service not found</h1>
        <Link to="/services" className="text-[#2f9fe6] font-semibold">View all services</Link>
      </div>
    );
  }

  const related = PROJECTS.filter((p) => p.tag === TAG_MAP[slug]).slice(0, 3);
  const otherServices = SERVICES.filter((s) => s.slug !== slug && s.category === "residential").slice(0, 5);

  return (
    <>
      <Seo title={service.title} path={`/services/${slug}`} description={service.short.slice(0, 155)} image={service.image} />
      <PageBanner
        crumb={service.title}
        title={service.title}
        subtitle="Quality craftsmanship, transparent pricing and a renovation experience built around the way you live."
        image={service.image}
      />

      <section className="py-24 bg-white overflow-hidden">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative reveal-left">
            <div className="shape-arch-top max-w-[480px]">
              <img src={service.image} alt={service.title} className="w-full h-[540px] object-cover" />
            </div>
            <div className="absolute -bottom-6 right-4 bg-[#2f9fe6] text-white px-7 py-6 rounded-2xl shadow-xl max-w-[220px]">
              <div className="font-display text-2xl font-bold">Built to last</div>
              <div className="text-white/90 text-sm">Made for everyday living</div>
            </div>
          </div>

          <div className="reveal-right">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Residential Service</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight mb-6">{service.title}</h2>
            <p className="text-[#6b7683] text-lg leading-relaxed mb-8">{service.short}</p>

            <div className="grid grid-cols-2 gap-3 mb-10">
              {service.points.map((p) => (
                <div key={p} className="flex items-center gap-2.5 text-[#0f2740]">
                  <span className="w-6 h-6 rounded-full bg-[#eaf5fd] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#2f9fe6]" />
                  </span>
                  <span className="font-medium text-sm">{p}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <button onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
                Get an Estimate <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/projects" className="px-8 py-4 rounded-full border-2 border-[#e2e7ec] text-[#0f2740] font-semibold hover:border-[#2f9fe6] hover:text-[#2f9fe6] transition-colors">
                View Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-[#f7f9fb]">
          <div className="container-x">
            <h2 className="font-display text-3xl font-bold text-[#0f2740] mb-10 reveal">Related projects</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((p, i) => (
                <div key={p.title} className={`reveal delay-${i + 1} group`}>
                  <div className="shape-soft relative mb-5">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-[280px] object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-[#2f9fe6] flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all"><ArrowUpRight className="w-5 h-5" /></span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-1">{p.title}</h3>
                  <p className="text-[#6b7683] text-sm">{p.area}, Edmonton</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-white">
        <div className="container-x">
          <h2 className="font-display text-2xl font-bold text-[#0f2740] mb-8 reveal">Explore other services</h2>
          <div className="flex flex-wrap gap-3">
            {otherServices.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="px-6 py-3 rounded-full bg-[#f2f5f8] text-[#0f2740] font-semibold hover:bg-[#2f9fe6] hover:text-white transition-colors">
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
